/**
 * TD Bundle Call-out
 * Web component for the bundle call-out on single-product PDPs.
 * Selecting the bundle and its options swaps the host section's product-form
 * variant id in place so Add to Cart adds the chosen pack variant.
 */
if (!customElements.get('td-bundle-callout')) {
  const SELECTORS = {
    choice: '[data-td-bundle-choice]',
    option: '[data-td-bundle-option]',
    variants: '[data-td-bundle-variants]',
    price: '[data-td-bundle-price]',
    comparePrice: '[data-td-bundle-compare-price]',
    savings: '[data-td-bundle-savings]',
    savingsAmount: '[data-td-bundle-savings-amount]',
    savingsPercent: '[data-td-bundle-savings-percent]',
    status: '[data-td-bundle-status]',
    simpleOptions: '[data-td-simple-bundle-options]',
    simpleLoading: '[data-td-simple-bundle-loading]',
    simpleColor: '[data-td-simple-bundle-color]',
    simpleSize: '[data-td-simple-bundle-size]',
    simpleGrid: '[data-td-simple-bundle-grid]',
    simpleControl: '[data-td-simple-bundle-control]',
    simpleProperty: '[data-td-simple-bundle-property]',
    selectorsHeading: '[data-td-bundle-selectors-heading]',
  };

  const COLOR_SWATCHES = {
    grey: '#80827f',
    gray: '#80827f',
    green: '#68705a',
    'tropical mist': '#79bac2',
  };

  customElements.define(
    'td-bundle-callout',
    class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.singleVariantId = this.dataset.singleVariantId;
        this.singleAvailable = this.dataset.singleAvailable === 'true';
        this.form = document.getElementById('product-form-' + this.sectionId);

        if (!this.form) return;

        this.variantInput = this.form.querySelector('input[name="id"]');
        this.productForm = this.form.closest('product-form');

        // The sticky Add to Cart renders a second product form; mirror the bundle
        // state onto it so it submits the same variant and bundle properties.
        this.stickyForm = document.getElementById('sticky-atc-form');
        this.stickyVariantInput = this.stickyForm?.querySelector('input[name="id"]');
        this.stickyProductForm = this.stickyForm?.closest('product-form');
        this.targetForms = [this.form, this.stickyForm].filter(Boolean);
        this.variantInputs = [this.variantInput, this.stickyVariantInput].filter(Boolean);

        this.choices = this.querySelectorAll(SELECTORS.choice);
        this.optionSelectors = this.querySelectorAll(SELECTORS.option);
        this.variants = this.parseVariants();
        this.bundleVariant = this.variants.find(({ id }) => String(id) === this.dataset.bundleVariantId);
        this.simpleOptionsContainer = this.querySelector(SELECTORS.simpleOptions);
        this.simpleBundleAvailable = true;

        this.onChange = this.onChange.bind(this);
        this.onFormVariantChange = this.onFormVariantChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.addEventListener('change', this.onChange);
        this.variantInput?.addEventListener('change', this.onFormVariantChange);
        // Capture phase guarantees the correct variant id is set before each
        // form's product-form handler reads it, even if the picker re-rendered.
        this.targetForms.forEach((form) => form.addEventListener('submit', this.onSubmit, true));

        this.updateBundleDetails();
        if (this.simpleOptionsContainer) this.loadSimpleBundleOptions();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.variantInput?.removeEventListener('change', this.onFormVariantChange);
        this.targetForms?.forEach((form) => form.removeEventListener('submit', this.onSubmit, true));
        if (this.applyFrame) window.cancelAnimationFrame(this.applyFrame);
        this.simpleBundleAbortController?.abort();
      }

      get bundleSelected() {
        const checked = Array.from(this.choices || []).find(({ checked }) => checked);
        return checked && checked.value === 'bundle';
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector(SELECTORS.variants)?.textContent || '[]');
        } catch (error) {
          console.error('Unable to parse bundle variants.', error);
          return [];
        }
      }

      onChange(event) {
        const simpleOptionChanged = event.target.matches(`${SELECTORS.simpleColor}, ${SELECTORS.simpleSize}`);

        if (event.target.matches(SELECTORS.option)) {
          const bundleChoice = Array.from(this.choices).find(({ value }) => value === 'bundle');
          if (bundleChoice && !bundleChoice.disabled) bundleChoice.checked = true;
          this.bundleVariant = this.getSelectedBundleVariant();
          this.updateBundleDetails();
        }

        if (simpleOptionChanged) {
          const bundleChoice = Array.from(this.choices).find(({ value }) => value === 'bundle');
          if (bundleChoice && !bundleChoice.disabled) bundleChoice.checked = true;
        }

        if (event.target.matches(SELECTORS.simpleColor)) {
          this.renderSimpleBundleSizes();
          this.syncSimpleBundleSelection();
        }

        if (event.target.matches(SELECTORS.simpleSize)) {
          this.syncSimpleBundleSelection();
        }

        if (
          !event.target.matches(
            `${SELECTORS.choice}, ${SELECTORS.option}, ${SELECTORS.simpleColor}, ${SELECTORS.simpleSize}`
          )
        ) {
          return;
        }

        this.applyVariant();
      }

      async loadSimpleBundleOptions() {
        this.simpleBundleAbortController = new AbortController();

        try {
          const response = await fetch(this.dataset.bundleUrl, {
            headers: { Accept: 'text/html' },
            signal: this.simpleBundleAbortController.signal,
          });
          if (!response.ok) throw new Error(`Bundle options request failed with status ${response.status}.`);

          const html = await response.text();
          this.simpleBundleFields = this.parseSimpleBundleFields(html);

          if (!this.simpleBundleFields.length) {
            this.enableBundleChoice();
            this.simpleOptionsContainer.hidden = true;
            return;
          }

          this.simpleBundleCombinations = this.getSimpleBundleCombinations(this.simpleBundleFields[0]);
          if (!this.simpleBundleCombinations.length) throw new Error('Bundle color and size options were not found.');

          this.renderSimpleBundleControls();
          this.syncSimpleBundleSelection();
          this.enableBundleChoice();
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Unable to load Simple Bundles options.', error);
          this.showSimpleBundleFallback();
        }
      }

      parseSimpleBundleFields(html) {
        const page = new DOMParser().parseFromString(html, 'text/html');
        // Simple Bundles owns this fetched markup, so its public ID and label structure are the available integration hooks.
        let optionsContainer = page.querySelector('#simple-bundles-io-options');

        if (!optionsContainer) {
          const markupMatch = html.match(/bundle_options_selector_html:\s*("(?:\\.|[^"\\])*")/);
          if (!markupMatch) return [];

          const markup = JSON.parse(markupMatch[1]);
          optionsContainer = new DOMParser()
            .parseFromString(markup, 'text/html')
            .querySelector('#simple-bundles-io-options');
        }

        if (!optionsContainer) return [];

        return Array.from(optionsContainer.querySelectorAll('select')).map((select) => {
          const options = Array.from(select.options)
            .filter(({ value }) => value)
            .map((option) => ({
              value: option.value,
              text: option.textContent.trim(),
              inventory: Number(option.dataset.inventory || 0),
              selected: option.selected,
            }));

          return {
            name: select.name,
            label: optionsContainer.querySelector(`label[for="${CSS.escape(select.id)}"]`)?.textContent.trim() || '',
            options,
            selected: options.find(({ selected }) => selected) || options[0],
          };
        });
      }

      getSimpleBundleCombinations(field) {
        return field.options
          .map((option) => {
            const optionDescription = option.value.includes(' - ')
              ? option.value.slice(option.value.indexOf(' - ') + 3)
              : option.text;
            const parts = optionDescription.split(' / ').map((part) => part.trim());

            if (parts.length < 2) return null;

            return {
              color: parts[0],
              size: parts[1],
              option,
            };
          })
          .filter(Boolean);
      }

      renderSimpleBundleControls() {
        const defaultCombination =
          this.simpleBundleCombinations.find(({ option }) => option.selected) || this.simpleBundleCombinations[0];
        const heading = this.simpleOptionsContainer.querySelector(SELECTORS.selectorsHeading);
        const grid = document.createElement('div');
        const colorSelector = this.createSimpleBundleSelector(
          'Color',
          SELECTORS.simpleColor.slice(1, -1),
          Array.from(new Set(this.simpleBundleCombinations.map(({ color }) => color))),
          defaultCombination.color,
          true
        );

        grid.className = 'td-bundle-callout__selectors-grid';
        grid.setAttribute(SELECTORS.simpleGrid.slice(1, -1), '');
        grid.append(colorSelector);
        this.simpleOptionsContainer.replaceChildren(heading, grid);
        this.renderSimpleBundleSizes(defaultCombination.size);
      }

      renderSimpleBundleSizes(preferredSize) {
        const color = this.getSimpleBundleControlValue(SELECTORS.simpleColor);
        const currentSize = this.getSimpleBundleControlValue(SELECTORS.simpleSize);
        const sizes = Array.from(
          new Set(
            this.simpleBundleCombinations
              .filter((combination) => combination.color === color && combination.option.inventory !== 0)
              .map(({ size }) => size)
          )
        );
        const selectedSize = sizes.includes(preferredSize)
          ? preferredSize
          : sizes.includes(currentSize)
            ? currentSize
            : sizes[0];
        const sizeSelector = this.createSimpleBundleSelector(
          'Pack Size',
          SELECTORS.simpleSize.slice(1, -1),
          sizes,
          selectedSize,
          false
        );
        const grid = this.simpleOptionsContainer.querySelector(SELECTORS.simpleGrid);
        const currentSizeSelector = this.querySelector(SELECTORS.simpleSize)?.closest(SELECTORS.simpleControl);

        currentSizeSelector?.remove();
        grid.append(sizeSelector);
      }

      createSimpleBundleSelector(labelText, hook, values, selectedValue, showSwatch) {
        const wrapper = document.createElement('fieldset');
        const legend = document.createElement('legend');
        const choices = document.createElement('div');

        wrapper.className = 'td-bundle-callout__selector';
        wrapper.setAttribute(SELECTORS.simpleControl.slice(1, -1), '');
        legend.className = 'td-bundle-callout__selector-label';
        legend.textContent = labelText;
        choices.className = 'td-bundle-callout__choice-list';

        values.forEach((value, index) => {
          const input = document.createElement('input');
          const label = document.createElement('label');
          const text = document.createElement('span');
          const id = `${hook}-${this.sectionId}-${index}`;

          input.id = id;
          input.type = 'radio';
          input.name = `${hook}-${this.sectionId}`;
          input.value = value;
          input.checked = value === selectedValue;
          input.className = 'visually-hidden td-bundle-callout__choice-input';
          input.setAttribute(hook, '');

          label.htmlFor = id;
          label.className = `td-bundle-callout__choice${showSwatch ? ' td-bundle-callout__choice--color' : ''}`;
          text.textContent = value;

          if (showSwatch) {
            const swatch = document.createElement('span');
            swatch.className = 'td-bundle-callout__swatch';
            swatch.style.setProperty('--td-bundle-swatch', COLOR_SWATCHES[value.toLowerCase()] || value);
            swatch.setAttribute('aria-hidden', 'true');
            label.append(swatch);
          }

          label.append(text);
          choices.append(input, label);
        });

        wrapper.append(legend, choices);

        return wrapper;
      }

      getSimpleBundleControlValue(selector) {
        return Array.from(this.querySelectorAll(selector)).find(({ checked }) => checked)?.value;
      }

      syncSimpleBundleSelection() {
        const color = this.getSimpleBundleControlValue(SELECTORS.simpleColor);
        const size = this.getSimpleBundleControlValue(SELECTORS.simpleSize);
        const combination = this.simpleBundleCombinations.find(
          (item) => item.color === color && item.size === size
        );
        const selections = [];

        this.targetForms.forEach((form) =>
          form.querySelectorAll(SELECTORS.simpleProperty).forEach((input) => input.remove())
        );
        this.simpleBundleAvailable = Boolean(combination && combination.option.inventory !== 0);
        if (!combination) {
          this.updateBundleDetails();
          return;
        }

        this.simpleBundleFields.forEach((field, index) => {
          let selectedOption = index === 0 ? combination.option : field.selected;

          if (index > 0) {
            const availableOptions = field.options.filter((option) => option.inventory !== 0);
            selectedOption =
              availableOptions.find((option) => option.value.toLowerCase().includes(size.toLowerCase())) ||
              availableOptions.find((option) => option === field.selected) ||
              availableOptions[0] ||
              selectedOption;
          }

          if (!selectedOption || selectedOption.inventory === 0) this.simpleBundleAvailable = false;
          if (selectedOption) {
            selections.push(selectedOption.value);
            this.addSimpleBundleProperty(field.name, selectedOption.value);
          }
        });

        this.addSimpleBundleProperty('properties[_bundle_selection]', selections.join(' <> '));
        this.setSimpleBundlePropertiesEnabled(this.bundleSelected);
        this.updateBundleDetails();
      }

      addSimpleBundleProperty(name, value) {
        this.targetForms.forEach((form) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          input.setAttribute(SELECTORS.simpleProperty.slice(1, -1), '');
          form.append(input);
        });
      }

      setSimpleBundlePropertiesEnabled(enabled) {
        this.targetForms.forEach((form) => {
          form.querySelectorAll(SELECTORS.simpleProperty).forEach((input) => {
            input.disabled = !enabled;
          });
        });
      }

      enableBundleChoice() {
        const bundleChoice = Array.from(this.choices).find(({ value }) => value === 'bundle');
        if (bundleChoice && this.dataset.bundleAvailable === 'true') bundleChoice.disabled = false;
      }

      showSimpleBundleFallback() {
        const loading = this.simpleOptionsContainer.querySelector(SELECTORS.simpleLoading);
        const link = document.createElement('a');

        loading.textContent = 'Choose your bundle options on the bundle page.';
        link.href = this.dataset.bundleUrl;
        link.className = 'link underlined-link';
        link.textContent = 'Customize bundle';
        this.simpleOptionsContainer.append(link);
      }

      onFormVariantChange() {
        this.singleVariantId = this.variantInput.value;
        this.singleAvailable = Boolean(this.singleVariantId) && !this.variantInput.disabled;

        if (!this.bundleSelected) return;

        // ProductInfo updates its button after changing this input. Re-apply the
        // bundle state on the next frame so the selected bundle remains authoritative.
        if (this.applyFrame) window.cancelAnimationFrame(this.applyFrame);
        this.applyFrame = window.requestAnimationFrame(() => this.applyVariant());
      }

      onSubmit() {
        // Re-assert on submit in case another script rewrote the input.
        this.applyVariant();
      }

      getSelectedBundleVariant() {
        const selectedOptions = Array.from(this.optionSelectors)
          .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
          .map(({ value }) => value);

        if (!selectedOptions.length) {
          return this.variants.find(({ id }) => String(id) === this.dataset.bundleVariantId);
        }

        return this.variants.find(
          ({ options }) =>
            options.length === selectedOptions.length &&
            options.every((option, index) => option === selectedOptions[index])
        );
      }

      updateBundleDetails() {
        const variant = this.bundleVariant;
        const comparePrice = this.querySelector(SELECTORS.comparePrice);
        const savings = this.querySelector(SELECTORS.savings);
        const status = this.querySelector(SELECTORS.status);

        if (variant) {
          this.querySelector(SELECTORS.price).textContent = variant.price;
          comparePrice.textContent = variant.compareAtPrice || '';
          comparePrice.hidden = !variant.compareAtPrice;

          this.querySelector(SELECTORS.savingsAmount).textContent = variant.savings
            ? `${this.dataset.savingsPrefix || 'Save'} ${variant.savings}`
            : '';
          this.querySelector(SELECTORS.savingsPercent).textContent = variant.savingsPercent
            ? `· ${variant.savingsPercent}%`
            : '';
          savings.hidden = !variant.savings;
        }

        status.hidden = Boolean(variant?.available && this.simpleBundleAvailable);
      }

      applyVariant() {
        if (!this.variantInput) return;

        const selectedVariant = this.bundleSelected ? this.bundleVariant : null;
        const variantId = this.bundleSelected ? selectedVariant?.id : this.singleVariantId;
        const productTitle = this.bundleSelected ? this.dataset.bundleTitle : this.dataset.singleTitle;
        const available = this.bundleSelected
          ? Boolean(selectedVariant?.available && this.simpleBundleAvailable)
          : this.singleAvailable;

        // Keep every target form (main + sticky) pointed at the resolved variant
        // so both submit the same selection regardless of which button is used.
        this.variantInputs.forEach((input) => {
          input.value = variantId || '';
          if (available) {
            input.removeAttribute('disabled');
          } else {
            input.setAttribute('disabled', 'disabled');
          }
        });

        this.setSimpleBundlePropertiesEnabled(this.bundleSelected && available);
        this.productForm?.toggleSubmitButton(!available, window.variantStrings?.soldOut);
        this.stickyProductForm?.toggleSubmitButton(!available, window.variantStrings?.soldOut);
        this.publishProductTitle(productTitle);
      }

      publishProductTitle(title) {
        if (!title || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        publish(PUB_SUB_EVENTS.bundleSelectionChange, {
          data: { title },
        });
      }
    }
  );
}
